import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Bell,
  BellOff, 
  Plus, 
  ChevronDown,
  Filter,
  Database,
  Sparkles,
  ExternalLink,
  File,
  GripVertical,
  Trash2,
  Wand2,
  ChevronLeft,
  Loader2,
  Edit2,
  Check,
  Info,
  Tag,
  Zap,
  Settings2,
  X,
  FileText,
  Gavel,
  Calendar,
  Clock,
  Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../components/ui/tooltip";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { TabsInCell, TabsInCellList, TabsInCellTrigger } from "../components/ui/tabs-in-cell-for-navigation";
import { Switch } from "../components/ui/switch";
import { motion, AnimatePresence, Reorder, LayoutGroup } from "framer-motion";
import { cn } from "../lib/utils";
import { useToast } from "../hooks/use-toast";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import apiClient from "../lib/api";
import React from "react";
import FilterInsightsModal from "../components/FilterInsightsModal";
import OnboardingModal from "../components/OnboardingModal";
import DismissModal from "../components/DismissModal";
import ChatWidget from "../components/ChatWidget";
import PulseCenter from "../components/PulseCenter";
import MunicipalityFilter from "../components/MunicipalityFilter";
import { useCategoriesStore } from "../stores/categoriesStore";
import { usePulsesFoundPeriodStore } from "../stores/pulsesFoundPeriodStore";
import { useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory } from "../hooks/useCategories";

export default function Acquisitions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedPulseItem, setSelectedPulseItem] = useState<any | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<any | null>(null);
  const [dismissItem, setDismissItem] = useState<{id: number, type: 'insight' | 'alert', title: string} | null>(null);
  const [subscribedInsights, setSubscribedInsights] = useState<Set<number>>(new Set());
  const [showChatWidget, setShowChatWidget] = useState(false);
  const [activeChatInsight, setActiveChatInsight] = useState<any | null>(null);
  
  // Use Zustand store for categories state
  const { 
    categories, 
    setActiveCategory, 
    reorderCategories,
    updateCategoryCounts,
    removeCategory: removeCategoryFromStore,
    updateCategory 
  } = useCategoriesStore();
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<('municipal' | 'provincial' | 'regional')[]>([]);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[]>([]);
  const [selectedProvStreams, setSelectedProvStreams] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'created' | 'event_date'>('event_date');
  const [meetingFilter, setMeetingFilter] = useState<'7days' | '30days' | 'all' | 'past'>('all');
  const [provincialFilter, setProvincialFilter] = useState<string>('all');
  const [showNeedsReview, setShowNeedsReview] = useState(false); // When false, hide pulses with insight meaningful_change_check === 'Needs Review'
  const { period: pulsesFoundPeriod, setPeriod: setPulsesFoundPeriod } = usePulsesFoundPeriodStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get municipality/source filter from URL (can be string for provincial sources or number for municipalities)
  const municipalityParam = searchParams.get('municipality');
  const selectedMunicipalityId = municipalityParam 
    ? (isNaN(Number(municipalityParam)) ? municipalityParam : Number(municipalityParam))
    : null;

  // Fetch combined dashboard data (includes stats, dashboard, and pinned). Counts respect Show Needs Review.
  const { data: pulseDashboardResponse } = useQuery({
    queryKey: ["pulses", "dashboard", "acquisitions", selectedMunicipalityId, showNeedsReview, pulsesFoundPeriod],
    queryFn: async () => {
      try {
        return await apiClient.pulses.getDashboard("acquisitions", {
          include_needs_review: showNeedsReview,
          period: pulsesFoundPeriod && pulsesFoundPeriod !== "all" ? pulsesFoundPeriod : undefined,
        });
      } catch (error) {
        console.error('[Acquisitions] Error fetching pulse dashboard:', error);
        return {
          stats: {
            new_pulses_today: 0,
            new_pulses_top3_match: 0,
            new_pulses_purpose: "How much new acquisition discovery intelligence did Cityscrape add today, and how much of it is decision-grade?",
            priority_items_count: 0,
            priority_next_date: null,
            priority_items_purpose: "What opportunity signals have upcoming dates soon, and what is the next key date to track?",
            high_impact_items_count: 0,
            high_impact_next_date: null,
            high_impact_purpose: "What is most commonly blocking or slowing acquisition opportunities right now?",
            relevance_percentage: "—%",
            relevance_top3_count: 0,
            relevance_purpose: "Is the acquisitions feed producing the kinds of signals this dashboard is designed to surface (growth frameworks, nodes, anchors, enabling commitments, dispositions, procurements)?"
          },
          dashboard: {
            municipal: { title: "Municipal", total_new: 0, sources: [] },
            regional: { title: "Regional", total_new: 0, sources: [] },
            provincial: { title: "Provincial", total_new: 0, sources: [] }
          },
          pinned: []
        };
      }
    },
    staleTime: 60000,
    refetchOnWindowFocus: true,
  });

  // Extract stats and dashboard data from combined response
  const dashboardStats = pulseDashboardResponse?.stats;
  const rawPulseDashboardData = pulseDashboardResponse?.dashboard;

  // Filter dashboard data by selected municipality
  const pulseDashboardData = React.useMemo(() => {
    if (!rawPulseDashboardData || !selectedMunicipalityId) {
      return rawPulseDashboardData;
    }

    // Filter sources to only show the selected municipality
    const filterSources = (sources: any[]) => {
      return sources
        .map((source: any) => {
          const matchesMunicipality = 
            source.id === selectedMunicipalityId.toString() ||
            source.id === selectedMunicipalityId ||
            source.municipality_id === selectedMunicipalityId;
          
          if (matchesMunicipality) {
            return source;
          }
          return null;
        })
        .filter((source: any) => source !== null);
    };

    return {
      municipal: {
        ...rawPulseDashboardData.municipal,
        sources: filterSources(rawPulseDashboardData.municipal?.sources || []),
        total_new: filterSources(rawPulseDashboardData.municipal?.sources || [])
          .reduce((sum: number, source: any) => sum + (source.total_new || 0), 0)
      },
      regional: {
        ...rawPulseDashboardData.regional,
        sources: filterSources(rawPulseDashboardData.regional?.sources || []),
        total_new: filterSources(rawPulseDashboardData.regional?.sources || [])
          .reduce((sum: number, source: any) => sum + (source.total_new || 0), 0)
      },
      provincial: rawPulseDashboardData.provincial
    };
  }, [rawPulseDashboardData, selectedMunicipalityId]);

  // Fetch categories using our existing hook (for Zustand store sync)
  const { data: categoriesData, isLoading: categoriesLoading, refetch: refetchCategories } = useCategories();
  
  // Mutations for creating, updating, and deleting categories
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  
  // Check if there are active filters
  const hasActiveFilters = 
    selectedLevels.length > 0 ||
    selectedMunicipalities.length > 0 ||
    selectedProvStreams.length > 0 ||
    selectedRegions.length > 0 ||
    sortBy !== 'event_date';

  // Get active category (only from followed categories)
  const activeCategory = categories.find(cat => cat.active && cat.is_followed !== false);
  
  // Extract filter options from pulse dashboard data
  const filterOptions = React.useMemo(() => {
    if (!pulseDashboardData) {
      return {
        municipalities: [],
        provincialStreams: [],
        regions: []
      };
    }

    const municipalities = new Set<string>();
    const provincialStreams = new Set<string>();
    const regions = new Set<string>();

    // Extract from municipal sources
    if (pulseDashboardData.municipal?.sources) {
      pulseDashboardData.municipal.sources.forEach((source: any) => {
        if (source.name) {
          municipalities.add(source.name);
        }
      });
    }

    // Extract from regional sources
    if (pulseDashboardData.regional?.sources) {
      pulseDashboardData.regional.sources.forEach((source: any) => {
        if (source.name) {
          regions.add(source.name);
        }
      });
    }

    // Extract from provincial sources
    if (pulseDashboardData.provincial?.sources) {
      pulseDashboardData.provincial.sources.forEach((source: any) => {
        if (source.name) {
          provincialStreams.add(source.name);
        }
      });
    }

    return {
      municipalities: Array.from(municipalities).sort(),
      provincialStreams: Array.from(provincialStreams).sort(),
      regions: Array.from(regions).sort()
    };
  }, [pulseDashboardData]);

  // No longer needed - PulseCenter handles all pulse display
  // Filtering is handled by PulseCenter's own queries
  
  // Format stats data for the 4 Acquisitions Dashboard cards
  const stats = [
    { 
      label: "New Acquisition Pulses", 
      value: String(dashboardStats?.new_pulses_today || 0),
      sub: `${dashboardStats?.new_pulses_top3_match || 0} Decision status`,
      purpose: dashboardStats?.new_pulses_purpose || "How much new acquisition discovery intelligence did Cityscrape add today, and how much of it is decision-grade?"
    },
    { 
      label: "Decision Windows (Next 60 Days)", 
      value: String(dashboardStats?.priority_items_count || 0),
      sub: dashboardStats?.priority_next_date 
        ? `Next key date: ${dashboardStats.priority_next_date}` 
        : "No upcoming dates",
      purpose: dashboardStats?.priority_items_purpose || "What opportunity signals have upcoming dates soon, and what is the next key date to track?"
    },
    { 
      label: "Constraint Signals (This Week)", 
      value: String(dashboardStats?.high_impact_items_count || 0),
      sub: dashboardStats?.high_impact_next_date 
        ? `Top constraint: ${dashboardStats.high_impact_next_date}` 
        : "Top constraint: —",
      purpose: dashboardStats?.high_impact_purpose || "What is most commonly blocking or slowing acquisition opportunities right now?"
    },
    { 
      label: "Signal Alignment (This Week)", 
      value: dashboardStats?.relevance_percentage || "—%",
      sub: `Aligned pulses: ${dashboardStats?.relevance_top3_count || 0}`,
      purpose: dashboardStats?.relevance_purpose || "Is the acquisitions feed producing the kinds of signals this dashboard is designed to surface (growth frameworks, nodes, anchors, enabling commitments, dispositions, procurements)?"
    },
  ];

  // Handle dismiss
  const handleDismiss = useCallback(async (itemId: number, reason: 'finished' | 'irrelevant') => {
    try {
      await apiClient.projectInsights.dismiss(itemId, reason);
      queryClient.invalidateQueries({ queryKey: ['project-insights-all'] });
      toast({
        title: "Insight dismissed",
        description: "The insight has been dismissed and removed from your view",
      });
      setDismissItem(null);
      setSelectedPulseItem(null);
    } catch (error: any) {
      toast({
        title: "Error dismissing insight",
        description: error?.message || "Failed to dismiss insight. Please try again.",
        variant: "destructive",
      });
    }
  }, [queryClient, toast]);

  return (
    <TooltipProvider>
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="flex-1 flex flex-col overflow-hidden p-8 max-w-[1800px] mx-auto w-full">
          
          {/* Header with Municipality Filter and Needs Review toggle */}
          <div className="flex items-center justify-between mb-6 flex-shrink-0 flex-wrap gap-3">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Acquisitions Dashboard</h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-600 dark:text-neutral-400 whitespace-nowrap">Pulses found:</span>
                <Select value={pulsesFoundPeriod} onValueChange={setPulsesFoundPeriod}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Time range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="last_7_days">Last 7 days</SelectItem>
                    <SelectItem value="last_30_days">Last 30 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div
                className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 cursor-pointer select-none"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setShowNeedsReview((prev) => {
                    const next = !prev;
                    console.log("[ShowNeedsReview] Acquisitions toggled:", prev, "->", next);
                    return next;
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowNeedsReview((prev) => !prev);
                  }
                }}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  <Switch checked={showNeedsReview} onCheckedChange={(c) => { console.log("[ShowNeedsReview] Acquisitions switch:", c); setShowNeedsReview(c); }} />
                </span>
                <span>Needs Review {showNeedsReview ? "(Showing)" : "(Hidden)"}</span>
              </div>
              <MunicipalityFilter />
            </div>
          </div>
          
          {/* Stats Grid - 4 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0 mb-8">
            {stats.map((stat, i) => (
                <div 
                  key={stat.label}
                  className="bg-[#1A1C1E] p-4 rounded-lg border border-white/10 shadow-sm flex flex-col justify-between h-28 relative group hover:border-[#BADA55]/50 transition-colors"
                >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">{stat.label}</div>
                    <motion.div 
                      key={`${stat.label}-value-${stat.value}`}
                      initial={false}
                      animate={{ opacity: 1 }}
                      className="text-3xl font-bold tracking-tight text-white"
                    >
                      {stat.value}
                    </motion.div>
                  </div>
                  {/* Info icon with tooltip */}
                  <div className="relative group/info">
                    <Info 
                      className="w-4 h-4 text-primary cursor-help shrink-0 ml-2" 
                    />
                    <div 
                      className="absolute right-0 top-full mt-2 w-80 p-3 text-xs rounded-[20px] opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-200 z-[100] pointer-events-none overflow-hidden"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(5px)',
                        WebkitBackdropFilter: 'blur(5px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.1), inset 0 0 0px 0px rgba(255, 255, 255, 0)'
                      }}
                    >
                      {/* Top gradient line */}
                      <div 
                        className="absolute top-0 left-0 right-0 h-px"
                        style={{
                          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)'
                        }}
                      ></div>
                      {/* Left gradient line */}
                      <div 
                        className="absolute top-0 left-0 w-px h-full"
                        style={{
                          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3))'
                        }}
                      ></div>
                      <div className="text-primary leading-relaxed relative z-10 font-semibold">{stat.purpose}</div>
                      {/* Arrow pointing up to icon */}
                      <div 
                        className="absolute bottom-full right-6 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-transparent"
                        style={{ borderBottomColor: 'rgba(255, 255, 255, 0.05)' }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center mt-1">
                  <motion.div 
                    key={`${stat.label}-sub-${stat.sub}`}
                    initial={false}
                    animate={{ opacity: 1 }}
                    className="text-xs text-neutral-400 truncate"
                  >
                    {stat.sub}
                  </motion.div>
                </div>
              </div>
            ))}
          </div>

          {/* Pulse Center - Three Column Layout with Pinned Pulses */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <PulseCenter 
              onOpenCategoryModal={() => setIsManageCategoriesOpen(true)} 
              pulseType="acquisitions"
              municipalityId={selectedMunicipalityId}
              showNeedsReview={showNeedsReview}
              foundDatePeriod={pulsesFoundPeriod && pulsesFoundPeriod !== "all" ? pulsesFoundPeriod : null}
            />
          </div>

        </div>
      </main>
      
      {/* Detailed Item Modal */}
      <Dialog open={!!selectedPulseItem} onOpenChange={(open) => !open && setSelectedPulseItem(null)}>
        <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden border-none shadow-2xl">
           {selectedPulseItem && (
             <>
               <DialogHeader className="sr-only">
                 <DialogTitle>{selectedPulseItem.title || 'Acquisition Pulse Item Details'}</DialogTitle>
                 <DialogDescription>View detailed information about this acquisition pulse item</DialogDescription>
               </DialogHeader>
               {/* Header */}
               <div className="p-6 bg-white dark:bg-neutral-900 border-b">
                 <div className="flex justify-between items-start gap-4 mb-4">
                    <h2 className="text-xl font-bold leading-snug text-neutral-900 dark:text-white">
                      {selectedPulseItem.title}
                    </h2>
                 </div>
               </div>
             </>
           )}
        </DialogContent>
      </Dialog>

      {/* Dismiss Modal */}
      {dismissItem && (
        <DismissModal
          itemId={dismissItem.id}
          itemTitle={dismissItem.title}
          itemType={dismissItem.type}
          onClose={() => setDismissItem(null)}
          onDismiss={handleDismiss}
        />
      )}

      {/* ChatWidget for AI Conversations */}
      {showChatWidget && activeChatInsight && (
        <ChatWidget
          insightId={activeChatInsight.id}
          documentTitle={activeChatInsight.title || 'Insight Document'}
          propertyName={activeChatInsight.municipalityName || 'Your Municipality'}
          onClose={() => {
            setShowChatWidget(false);
            setActiveChatInsight(null);
          }}
        />
      )}

        </div>
    </TooltipProvider>
  );
}

